import type {
  AwardCriterionType,
  AwardMethodDto,
  EvaluationDto,
} from "../../../../autogen/bff-api";

export interface BidEvaluationCriterion {
  id: string;
  name: string | null;
  type: AwardCriterionType;
  isPrice: boolean;
  weightPercent: number | null;
  points: number | null;
  weightedPoints: number | null;
}

export type BidEvaluationSummary =
  | { state: "loading" }
  | { state: "error" }
  | { state: "no-criteria" }
  | { state: "not-evaluated"; totalBids: number }
  | { state: "missing-from-eval" }
  | {
      state: "evaluated";
      rank: number;
      totalBids: number;
      weightedTotal: number;
      awardMethod: AwardMethodDto | null;
      criteria: BidEvaluationCriterion[];
    };

export const deriveBidEvaluationSummary = (
  evaluation: EvaluationDto | undefined,
  bidId: string,
  meta: { isLoading: boolean; isError: boolean },
): BidEvaluationSummary => {
  if (meta.isLoading) return { state: "loading" };
  if (meta.isError || !evaluation) return { state: "error" };
  if (evaluation.criteria.length === 0) return { state: "no-criteria" };

  const bid = evaluation.bids.find((b) => b.bidId === bidId);
  if (!bid) return { state: "missing-from-eval" };

  // BFF's progress.filled only counts quality scores, so it's permanently 0 for
  // LOWEST_PRICE procurements (qualityCriteria is empty). And the BFF emits one
  // EvalCellDto per criterion per bid unconditionally (criteria.map in
  // EvaluationDtos.kt), so cells.length is always > 0 once criteria exist.
  // The real per-bid signal is whether any cell carries a non-null points value,
  // or whether the calculator produced a non-zero weighted total (auto price).
  const hasMeaningfulScore =
    bid.weightedTotal > 0 || bid.cells.some((cell) => cell.points != null);
  if (!hasMeaningfulScore) {
    return { state: "not-evaluated", totalBids: evaluation.bids.length };
  }

  const cellsByCriterion = new Map(bid.cells.map((c) => [c.criterionId, c]));

  return {
    state: "evaluated",
    rank: bid.rank,
    totalBids: evaluation.bids.length,
    weightedTotal: bid.weightedTotal,
    awardMethod: evaluation.awardMethod ?? null,
    criteria: evaluation.criteria.map((criterion) => {
      const cell = cellsByCriterion.get(criterion.id);
      return {
        id: criterion.id,
        // Qualified with its criterion, and weighted by its share of the whole procurement — a
        // sub-criterion read on its own would otherwise be both unnamed and overstated.
        name: criterion.parentName
          ? `${criterion.parentName} — ${criterion.name?.trim() || criterion.type}`
          : criterion.name?.trim() || null,
        type: criterion.type,
        isPrice: criterion.isPrice,
        weightPercent: criterion.effectiveWeightPercent ?? criterion.weightPercent ?? null,
        points: cell?.points ?? null,
        weightedPoints: cell?.weightedPoints ?? null,
      };
    }),
  };
};

export const formatRankLabel = (
  summary: BidEvaluationSummary,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string =>
  summary.state === "evaluated"
    ? t("#{{rank}} of {{totalBids}} bids", { rank: summary.rank, totalBids: summary.totalBids })
    : "–";
